import type { AxiosError } from 'axios';
import { CanceledError } from 'axios';
import cookies from 'js-cookie';
import { orderBy } from 'lodash-es';

import { dashboardBffClient } from '@/api-client';
import type { PolicyP } from '@/api-client/api';
import { useUserStore } from '@/stores/user';
import { getFlattenedErrors } from '@/utils';

import { showFlash } from './alerts';
import { permissionsErrorHandler } from './authorization';
import constants from './constants';
import * as log from './logger';
import router from './router';
import { useAppInfoStore } from './stores/appInfo';
import { useMainStore } from './stores/main';

/**
 * Print debug info to console
 */
export function outputDebugInfo(error: AxiosError): void {
  log.groupCollapsed('Network error debug info');

  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    log.error('Response data', error.response.data);
    log.error('Response status', error.response.status);
    log.error('Response headers', error.response.headers);
  } else if (error.request) {
    // The request was made but no response was received
    log.error('Request', error.request);
  } else {
    // Something happened in setting up the request that triggered an Error
    log.error('Error setting up network request', error.message);
  }
  log.groupEnd();
}

function unauthenticatedUserError401(): void {
  const userStore = useUserStore();
  const appInfoStore = useAppInfoStore();
  const authCookieName = `${appInfoStore.systemInfo?.clusterId}-auth`;
  const authCookieContents = cookies.get(authCookieName);
  if (!authCookieContents) {
    log.debug('Not token/OIDC auth. No cookie set.');
    return;
  }
  log.info('Clearing auth cookie and redirecting to logout page.');
  cookies.set(authCookieName, '', { path: '/' });
  router.push({ name: 'Logout' });
  userStore.logOut();
}

function accessDeniedError403(error: AxiosError): void {
  const data = error.response?.data as Record<string, unknown> | undefined;
  const details =
    (data?.message as string) ??
    mostNestedError(data) ??
    'User does not have the required permissions';

  log.warn('Showing access denied message for', error.config?.url);
  showFlash({
    message: 'Access Denied',
    details,
    messageType: 'info',
    icon: 'icon-lock',
    dismiss: 6000,
  });
}

function mostNestedError(errorStack: unknown): string | null {
  if (!errorStack) return null;
  const errors = getFlattenedErrors(errorStack);
  return errors?.length > 0 ? errors[errors.length - 1] : null;
}

function slowServiceError(error: AxiosError, svcName: string): void {
  const request = error.request as { timeout: number } | undefined;
  showFlash({
    message: 'Slow Connection',
    details: `One of the services ${svcName} is slow to respond. ${constants.messages.SUPPORT}`,
    messageType: 'info',
    icon: 'icon-turtle',
    dismiss: (request?.timeout ?? 0) + 4000,
  });
  log.error(error.message, error.config?.url);
}

/**
 * Handle error response generically. Show an error message.
 */
export function defaultErrorHandler(error: AxiosError): void {
  const statusCode = error.response?.status;
  const svc = /\w+-svc/.exec(error.config?.url ?? '');
  const svcName = svc?.[0] ? `(${svc[0]})` : '';

  if (statusCode === 401) {
    unauthenticatedUserError401();
    return;
  }
  if (statusCode === 403) {
    accessDeniedError403(error);
    return;
  }
  if (error.message?.startsWith('timeout')) {
    slowServiceError(error, svcName);
    return;
  }
  if (error instanceof CanceledError) {
    log.error('Request was canceled', error.message);
    return;
  }
  if (statusCode !== undefined && statusCode >= 500) {
    if (
      // special case for location profile create/edit 500 errors
      // we need to be picky when we use this for other pages
      // avoid showing errors on pages with polling to prevent flash spamming
      // TODO: THIS IS A TEMPORARY FIX AND NEEDS TO BE ADDRESSED IN THE BFF
      [
        'MCLocationProfileCreate',
        'MCLocationProfileEdit',
        'LocationProfileCreate',
        'LocationProfileEdit',
      ].includes(router.currentRoute.value.name as string)
    ) {
      showFlash({
        message: 'Server Error',
        details: `There was a problem with this location profile. ${constants.messages.SUPPORT}`,
        messageType: 'danger',
        icon: 'icon-disconnect',
      });
    }
    log.error('Server Error', error.message);
    return;
  }
  const responseData = error.response?.data as Record<string, unknown> | undefined;
  showFlash({
    message: 'Connection Error',
    details: `${
      (responseData?.message as string) ??
      error.message ??
      mostNestedError(error) ??
      'There was a problem connecting to the server'
    } ${svcName}`,
    messageType: 'danger',
    icon: 'icon-disconnect',
    dismiss: 'user',
  });
  outputDebugInfo(error);
}

/**
 * Handle error response silently. Log the error message.
 */
export function silentErrorHandler(error: AxiosError): void {
  outputDebugInfo(error);
}

/**
 * Fetch policy CRs
 */
export async function fetchPolicies(
  params?: { ns?: string } & Record<string, unknown>,
): Promise<PolicyP[]> {
  const store = useMainStore();
  const appInfoStore = useAppInfoStore();

  // If we're on the policies page, don't poll the policycrs endpoint, we will poll the policy details endpoint directly on the policies page instead
  if (window.location.hash.includes('policies') && !window.location.hash.includes('presets')) {
    return [];
  }

  const { ns: namespace = appInfoStore.systemNamespace, ...query } = params ?? {};
  try {
    const { data } = await dashboardBffClient.bff.bffListPolicyCRs(namespace, query);
    if (!Array.isArray(data)) {
      // On initial page load, on the login page for token auth cluster,
      // we've seen response.data contain an HTML string. Not sure why.
      log.warn(`fetchPolicies response.data is not an array`, data);
      return [];
    }
    if (namespace === appInfoStore.systemNamespace) store.policies = data;
    return data;
  } catch (err) {
    permissionsErrorHandler(err as AxiosError, 'policies', 'list');
    return [];
  }
}

/**
 * Fetch distributions and save to central state
 */
export function fetchDistributions() {
  const store = useMainStore();
  return dashboardBffClient.mc
    .listDistributions()
    .then((response) => {
      let distns = response.data || [];
      distns = orderBy(distns, [(d) => d.metadata?.name], ['asc']);
      store.distributions = distns;
      return distns;
    })
    .catch(defaultErrorHandler);
}

/**
 * Fetch dead namespaces
 */
export async function fetchRemovedNamespaces() {
  return dashboardBffClient.namespaces
    .listNamespaces({ live: false })
    .then((response) => {
      if (response.data.loading) return null;
      return response.data.namespaces;
    })
    .catch((err) => {
      permissionsErrorHandler(err as AxiosError, 'applications', 'list');
    });
}

/**
 * Fetch selectors
 */
export function fetchSelectors() {
  return dashboardBffClient.selectors
    .listSelectors()
    .then((response) => response.data)
    .catch(defaultErrorHandler);
}
